import { useState, useRef, useContext, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import AuthContext from "../../contexts/AuthContext";
import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "../../utils/inventoryApi";

export default function Supplier() {
  const { restaurantId } = useContext(AuthContext);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    phone: "",
    email: "",
    address: "",
    gstin: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const toastTimeout = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  useEffect(() => {
    if (restaurantId) {
      fetchSuppliers();
    }
  }, [restaurantId]);

  async function fetchSuppliers() {
    setLoading(true);
    try {
      const data = await getSuppliers(restaurantId);
      setSuppliers(data || []);
    } catch (err) {
      showToast("Failed to fetch suppliers", "error");
    }
    setLoading(false);
  }

  function openModal(mode = "add", supplier = null) {
    setModalMode(mode);
    setSelectedSupplier(supplier);
    setFormErrors({}); // Clear previous errors

    if (mode === "edit" && supplier) {
      setFormData({
        name: supplier.name || "",
        contact: supplier.contact || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        address: supplier.address || "",
        gstin: supplier.gstin || "",
      });
    } else {
      setFormData({
        name: "",
        contact: "",
        phone: "",
        email: "",
        address: "",
        gstin: "",
      });
    }
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedSupplier(null);
    setFormErrors({});
    setFormData({
      name: "",
      contact: "",
      phone: "",
      email: "",
      address: "",
      gstin: "",
    });
  }

  // Check if supplier name already exists (for real-time validation)
  function isSupplierNameDuplicate(name, excludeId = null) {
    if (!name) return false;
    return suppliers.some(
      (supplier) =>
        supplier.name &&
        supplier.name.toLowerCase() === name.toLowerCase() &&
        supplier.id !== excludeId
    );
  }

  // Validate form data
  function validateForm() {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Supplier name is required";
    } else if (isSupplierNameDuplicate(formData.name, selectedSupplier?.id)) {
      errors.name = `Supplier "${formData.name}" already exists`;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      errors.phone = "Please enter a valid phone number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // Email validation
  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Phone validation
  function isValidPhone(phone) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      if (modalMode === "add") {
        await createSupplier(restaurantId, formData);
        showToast("Supplier added successfully!");
      } else {
        await updateSupplier(restaurantId, selectedSupplier.id, formData);
        showToast("Supplier updated successfully!");
      }
      await fetchSuppliers();
      closeModal();
    } catch (err) {
      console.error("Error saving supplier:", err);
      // Handle specific validation errors from backend
      if (err.response && err.response.data && err.response.data.error) {
        showToast(err.response.data.error, "error");
      } else {
        showToast("Failed to save supplier", "error");
      }
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this supplier?"))
      return;
    try {
      await deleteSupplier(restaurantId, id);
      await fetchSuppliers();
      showToast("Supplier deleted successfully!");
    } catch (err) {
      console.error("Error deleting supplier:", err);
      if (err.response && err.response.data && err.response.data.error) {
        showToast(err.response.data.error, "error");
      } else {
        showToast("Failed to delete supplier", "error");
      }
    }
  }

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(
      () => setToast({ show: false, message: "", type: "" }),
      3000
    );
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Suppliers
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
            Manage your suppliers for inventory purchases
          </p>
        </div>
        <button
          onClick={() => openModal("add")}
          className="bg-rose-500 hover:bg-rose-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
        >
          <FaPlus />
          <span className="hidden sm:inline">Add Supplier</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-50 dark:bg-slate-700">
            <tr>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Name
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Address
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                GSTIN
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
            {suppliers.map((supplier) => (
              <tr key={supplier.id}>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-800 dark:text-white">
                  {supplier.name}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {supplier.contact}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {supplier.phone}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {supplier.email}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {supplier.address}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {supplier.gstin}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-right">
                  <button
                    onClick={() => openModal("edit", supplier)}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-1 sm:mr-2 p-1"
                  >
                    <FaEdit className="text-sm sm:text-base" />
                  </button>
                  <button
                    onClick={() => handleDelete(supplier.id)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                  >
                    <FaTrash className="text-sm sm:text-base" />
                  </button>
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 sm:px-4 py-6 text-center text-gray-400 dark:text-gray-500 text-sm sm:text-base"
                >
                  No suppliers found. Click "Add Supplier" to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-lg p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
                  {modalMode === "add" ? "Add Supplier" : "Edit Supplier"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 text-xl sm:text-2xl"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm ${
                        formErrors.name
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-300 dark:border-slate-600"
                      }`}
                      placeholder="Enter supplier name"
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                      placeholder="Contact person name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm ${
                        formErrors.phone
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-300 dark:border-slate-600"
                      }`}
                      placeholder="Phone number"
                    />
                    {formErrors.phone && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm ${
                        formErrors.email
                          ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                          : "border-gray-300 dark:border-slate-600"
                      }`}
                      placeholder="Email address"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                      placeholder="Full address"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      GSTIN
                    </label>
                    <input
                      type="text"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                      placeholder="GSTIN number"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-3 sm:pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-rose-500 hover:bg-rose-600 text-white py-2 px-4 rounded-lg transition-colors text-sm"
                  >
                    {modalMode === "add" ? "Add Supplier" : "Update Supplier"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 dark:bg-slate-600 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-4 right-4 px-4 sm:px-6 py-3 rounded-lg text-white text-sm ${
              toast.type === "error" ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
